import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Radio, Calendar, ExternalLink, Pencil, X, Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatnotShowsSection({ token }) {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", scheduled_at: "", description: "", whatnot_url: "" });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchShows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/whatnot-shows`, { headers });
      setShows(res.data);
    } catch (err) {
      toast.error("Failed to load shows");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchShows(); }, [fetchShows]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/whatnot-shows/sync`, {}, { headers });
      toast.success(res.data.message || "Synced from Whatnot");
      fetchShows();
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setForm({ title: "", scheduled_at: "", description: "", whatnot_url: "" });
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.scheduled_at) {
      toast.error("Title and date/time are required");
      return;
    }

    try {
      const isoDate = new Date(form.scheduled_at).toISOString();
      const payload = { ...form, scheduled_at: isoDate };

      if (editingId) {
        await axios.put(`${API}/api/whatnot-shows/${editingId}`, payload, { headers });
        toast.success("Show updated");
      } else {
        await axios.post(`${API}/api/whatnot-shows`, payload, { headers });
        toast.success("Show created");
      }
      resetForm();
      fetchShows();
    } catch (err) {
      toast.error("Failed to save show");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    try {
      await axios.delete(`${API}/api/whatnot-shows/${id}`, { headers });
      toast.success("Show deleted");
      fetchShows();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleToggleLive = async (id) => {
    try {
      await axios.put(`${API}/api/whatnot-shows/${id}/live`, {}, { headers });
      toast.success("Live status toggled");
      fetchShows();
    } catch (err) {
      toast.error("Failed to toggle live");
    }
  };

  const startEdit = (show) => {
    const localDate = new Date(show.scheduled_at);
    const offset = localDate.getTimezoneOffset();
    const local = new Date(localDate.getTime() - offset * 60000);
    setForm({
      title: show.title,
      scheduled_at: local.toISOString().slice(0, 16),
      description: show.description || "",
      whatnot_url: show.whatnot_url || "",
    });
    setEditingId(show.id);
    setShowForm(true);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + 
      ", " + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const isUpcoming = (iso) => new Date(iso) > new Date();

  return (
    <div className="space-y-4" data-testid="whatnot-shows-section">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-gray-900">Whatnot Shows</h3>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
            className="text-gray-600"
            data-testid="sync-whatnot-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? "Syncing..." : "Sync from Whatnot"}
          </Button>
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-[#FFE505] hover:bg-[#e6ce04] text-[#292527]"
            data-testid="add-show-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Show
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-400">Shows auto-sync from your Whatnot profile every 30 minutes, or tap Sync.</p>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3" data-testid="show-form">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-orange-800">
              {editingId ? "Edit Show" : "New Show"}
            </span>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <Input
            placeholder="Show title (e.g., ALL NEW WITH TAGS!)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="show-title-input"
          />
          <Input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
            data-testid="show-datetime-input"
          />
          <Input
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="show-description-input"
          />
          <Input
            placeholder="Whatnot URL (optional)"
            value={form.whatnot_url}
            onChange={(e) => setForm({ ...form, whatnot_url: e.target.value })}
            data-testid="show-url-input"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-[#FFE505] hover:bg-[#e6ce04] text-[#292527]" data-testid="save-show-btn">
              <Check className="w-4 h-4 mr-1" /> {editingId ? "Update" : "Create"}
            </Button>
            <Button size="sm" variant="outline" onClick={resetForm}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Shows List */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : shows.length === 0 ? (
        <p className="text-sm text-gray-500">No shows scheduled. Add one to highlight it on your homepage.</p>
      ) : (
        <div className="space-y-2">
          {shows.map((show) => (
            <div
              key={show.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                show.is_live
                  ? 'bg-red-50 border-red-200'
                  : isUpcoming(show.scheduled_at)
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-gray-50 border-gray-200 opacity-60'
              }`}
              data-testid={`show-item-${show.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900 truncate">{show.title}</span>
                  {show.is_live && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      Live
                    </span>
                  )}
                  {show.source === "whatnot_auto" && (
                    <span className="px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-semibold">
                      Auto
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <Calendar className="w-3 h-3" />
                  {formatDate(show.scheduled_at)}
                </div>
                {show.description && (
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{show.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                <button
                  onClick={() => handleToggleLive(show.id)}
                  className={`p-1.5 rounded-md transition-colors ${
                    show.is_live
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  title={show.is_live ? "End live" : "Go live"}
                  data-testid={`toggle-live-${show.id}`}
                >
                  <Radio className="w-4 h-4" />
                </button>
                <button
                  onClick={() => startEdit(show)}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                  title="Edit"
                  data-testid={`edit-show-${show.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {show.whatnot_url && (
                  <a
                    href={show.whatnot_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                    title="Open on Whatnot"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => handleDelete(show.id)}
                  className="p-1.5 rounded-md bg-gray-100 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Delete"
                  data-testid={`delete-show-${show.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
